import camelize from 'camelize';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import {
  newToastNotification,
  ToastType,
} from '../../dashboard/components/ToastNotifications/toasts';
import joinArray from '../../utils/joinArray';
import { serializeFilters, serializeOrdering } from '../../utils/serialization';
import { camelToSnake } from '../../dashboard/utils';
import { baseQueryWithToast } from './base';
import { setMissingVariants } from '../actions/inventoryPricingActions';

const inventoryAdapter = createEntityAdapter({
  selectId: (entry) => entry.variant_pk,
});

const initialState = inventoryAdapter.getInitialState();
const latestOrderCellRequestIds = new Map();

// trust the actual url path instead of prepending it because ugh
const actualPathUrl = (url) => `${window.location.origin}${url}`;

export const vendorApi = createApi({
  reducerPath: 'vendor',
  baseQuery: baseQueryWithToast,
  tagTypes: ['GetOrders'],
  endpoints: (builder) => ({
    bulkPostAccounting: builder.mutation({
      invalidatesTags: ['GetOrders'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const response = await queryFulfilled;
          const ordersByResult = Object.groupBy(
            response?.data?.orders || [],
            ({ status }) =>
              status === 200 ? ToastType.SUCCESS : ToastType.ERROR
          );

          Object.keys(ordersByResult).forEach((toastType) =>
            ordersByResult[toastType].forEach(({ error, statusMessage }) =>
              newToastNotification({
                body: [statusMessage, error].filter(Boolean).join(': '),
                toastType,
              })
            )
          );
        } catch {
          // error toast handled in base query
        }
      },
      query: ({ orderIds, url }) => ({
        body: { order_ids: orderIds },
        method: 'POST',
        url: actualPathUrl(url),
      }),
    }),

    bulkUpdateOrders: builder.mutation({
      extraOptions: { handlesErrors: true },
      invalidatesTags: (_result, _error, { refresh }) =>
        refresh !== false && ['GetOrders'], // only suppress refresh on explicit false
      async onQueryStarted({ refresh }, { queryFulfilled }) {
        try {
          const response = await queryFulfilled;

          if (
            refresh === false &&
            !response?.data?.message &&
            !response?.data?.error
          ) {
            return;
          }

          const successData = response?.data;
          const successMessage = Array.isArray(successData)
            ? successData.map((s) => s?.message).join(', ')
            : successData?.message;

          newToastNotification({
            body: successMessage || 'Orders updated',
            toastType: ToastType.SUCCESS,
          });
        } catch (response) {
          const errorData = response?.error?.data;
          const errorMessage = Array.isArray(errorData)
            ? errorData.map((e) => e?.message).join(', ')
            : errorData?.message;

          newToastNotification({
            body: errorMessage || 'Orders could not be updated',
            toastType: ToastType.ERROR,
          });
        }
      },
      // ... need refresh for checking in invalidateTags above
      // eslint-disable-next-line no-unused-vars
      query: ({ data, url, refresh = true }) => {
        const body = new FormData();
        Object.keys(data).forEach((key) =>
          Array.isArray(data[key])
            ? data[key].forEach((value) => body.append(key, value))
            : body.append(key, data[key])
        );
        return {
          body,
          // TODO: convert this to JSON once old order list page is removed
          formData: true,
          headers: {
            Accept: 'application/json',
            'X-REQUESTED-WITH': 'XMLHttpRequest',
          },
          method: 'POST',
          url: actualPathUrl(url),
        };
      },
    }),

    exportCsv: builder.query({
      query: ({ url }) => ({
        headers: {
          Accept: 'application/json',
          'X-REQUESTED-WITH': 'XMLHttpRequest',
        },
        method: 'GET',
        url: actualPathUrl(url),
      }),
      transformResponse: camelize,
    }),

    getBatches: builder.query({
      query: ({ filters, page, pageSize, vendorId, ordering }) => {
        const sortMap = {
          products: 'products__name',
          type: 'bucket_weight',
          attachments: 'attachments_count',
        };
        return {
          url: `api/vendor/${vendorId}/batches/?${serializeFilters(
            camelToSnake(filters),
            { sample_type: 'commaDelimited' }
          )}`,
          method: 'GET',
          params: {
            page_number: page,
            page_size: pageSize,
            ordering: serializeOrdering(
              ordering.map((i) => ({ ...i, id: sortMap[i.id] || i.id }))
            ),
          },
        };
      },
      transformResponse: (response) => response,
    }),

    getBrands: builder.query({
      query: ({ vendorId }) => `api/vendor/${vendorId}/brands/`,
      transformResponse: (response) => response.brands,
    }),

    getCategories: builder.query({
      query: ({ vendorId }) => `api/vendor/${vendorId}/categories/`,
      transformResponse: (response) => response.categories,
    }),

    getDownloadQueues: builder.query({
      query: ({ downloadQueueIds, params, url }) => ({
        method: 'GET',
        params,
        url: `${actualPathUrl(url)}?${[]
          .concat(downloadQueueIds)
          .map((id) => `id=${id}`)
          .join('&')}`,
      }),
      transformResponse: camelize,
    }),

    getSizes: builder.query({
      query: ({ vendorId }) => `api/vendor/${vendorId}/inventory-unit-sizes/`,
      transformResponse: (response) =>
        response.inventory_sizes.map(({ key, name }) => ({
          id: key,
          name,
        })),
    }),

    getStockLocations: builder.query({
      query: ({ vendorId, params }) => ({
        url: `vendor/${vendorId}/stock-locations/`,
        params,
      }),
      transformResponse: (response) => response.stock_locations,
    }),

    getProductAttributes: builder.query({
      query: ({ vendorId }) => ({
        url: `api/vendor/${vendorId}/products/product-attributes`,
        params: {
          slug: 'sample',
        },
      }),
      transformResponse: (response) => {
        if (response.results && response.results.length > 0) {
          return response.results[0].values;
        }
        return [];
      },
    }),

    getInventoryPrices: builder.query({
      query: ({ vendorId, page, pageSize, ordering, filters }) => ({
        url: `vendor/${vendorId}/products/inventory-prices/?${serializeFilters(
          filters,
          { sample_type: 'commaDelimited', size: 'commaDelimited' }
        )}`,
        method: 'GET',
        params: {
          page,
          page_size: pageSize,
          ordering: serializeOrdering(ordering),
        },
      }),
      transformResponse: (response) => {
        const normalized = inventoryAdapter.setAll(
          initialState,
          response.results
        );

        return {
          ...response,
          entities: normalized.entities,
          ids: normalized.ids,
        };
      },
      merge: (currentCache, newItems) => {
        currentCache.entities = {
          ...currentCache.entities,
          ...newItems.entities,
        };
        currentCache.ids = newItems.ids;
        currentCache.count = newItems.count;
        currentCache.next = newItems.next;
        currentCache.previous = newItems.previous;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: 'InventoryEntry', id })),
              { type: 'InventoryList' },
            ]
          : [{ type: 'InventoryList' }],
    }),

    getLogEntries: builder.query({
      query: ({ filters, page, pageSize, vendorId }) => ({
        url: `api/vendor/${vendorId}/log-entries/?${serializeFilters(
          camelToSnake(filters)
        )}`,
        method: 'GET',
        params: {
          page,
          page_size: pageSize,
        },
      }),
      transformResponse: camelize,
    }),

    getOrders: builder.query({
      providesTags: ['GetOrders'],
      query: ({ params, url }) => ({
        method: 'GET',
        params,
        url: actualPathUrl(url),
      }),
      transformResponse: camelize,
    }),

    getOrdersCount: builder.query({
      query: ({ params, url }) => ({
        method: 'GET',
        params,
        url: actualPathUrl(url),
      }),
    }),

    getProducts: builder.query({
      query: ({ filters, page, pageSize, vendorId, ordering }) => {
        const sortMap = {
          categories: 'categories__name',
          brands: 'brands__name',
        };
        return {
          url: `api/vendor/${vendorId}/products/?${serializeFilters(
            camelToSnake(filters),
            { sample_type: 'commaDelimited' }
          )}`,
          method: 'GET',
          params: {
            page_number: page,
            page_size: pageSize,
            ordering: serializeOrdering(
              ordering.map((i) => ({ ...i, id: sortMap[i.id] || i.id }))
            ),
          },
        };
      },
      transformResponse: (response) => response,
    }),

    getSettings: builder.query({
      query: ({ vendorId, settings }) => ({
        url: `api/vendor/${vendorId}/settings/`,
        method: 'GET',
        params: {
          setting: settings,
        },
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        return `${endpointName}-${queryArgs.vendorId}`;
      },
      merge: (currentCache, newItems) => {
        return { ...currentCache, ...newItems };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;

        const newSettings = currentArg.settings.filter(
          (setting) => !previousArg.settings.includes(setting)
        );

        return newSettings.length > 0;
      },
    }),

    getSyncStatusWebSocketUrl: builder.query({
      query: ({ vendorId }) => ({
        url: `vendor/${vendorId}/products/inventory-prices/websocket_url/`,
        method: 'GET',
      }),
      transformResponse: (response) => response.websocket_url,
    }),

    getFSOUserActivityWebSocketUrl: builder.query({
      query: ({ orderId }) => ({
        url: `api/websockets/`,
        method: 'POST',
        body: {
          socket_type: 'seller-ordering-user-action',
          order_id: orderId,
        },
      }),
      transformResponse: (response) => response.websocket_url,
    }),

    getInvoiceSettings: builder.query({
      query: ({ vendorId }) => ({
        url: `api/vendor/${vendorId}/invoice/settings/`,
        method: 'GET',
        params: {
          setting: 'order_invoice_settings',
        },
      }),
      transformResponse: (response) => {
        // Extract order_invoice_settings from response or use defaults
        const orderInvoiceSettings =
          response?.order_invoice_settings || response;
        const columnsSettings = orderInvoiceSettings?.columns_settings;

        return {
          ...orderInvoiceSettings,
          columns_settings:
            Array.isArray(columnsSettings) && columnsSettings.length > 0
              ? columnsSettings
              : [],
        };
      },
    }),

    getOrderInvoice: builder.query({
      query: ({ vendorId, orderId }) => ({
        url: `api/vendor/${vendorId}/order-invoice/${orderId}/`,
        method: 'GET',
      }),
    }),

    getStrains: builder.query({
      query: ({ vendorId }) => `api/vendor/${vendorId}/strains/`,
      transformResponse: (response) => response,
    }),

    getInventoryImportCapabilities: builder.query({
      query: ({ vendorId, params }) =>
        `vendors/${vendorId || params.vendorId}/inventory-import-capabilities/`,
      transformResponse: ({ capabilities }) => capabilities,
    }),

    getIntegrationMappings: builder.query({
      query: ({
        vendorId,
        capabilityId,
        productName,
        externalValue,
        pageIndex,
        pageSize,
        ordering,
      }) => {
        const sortMap = {
          product_name: 'product__name',
          product_id: 'product__id',
        };
        return {
          url: `vendors/${vendorId}/capabilities/${capabilityId}/integration-mappings/`,
          method: 'GET',
          params: {
            pageIndex,
            pageSize,
            productName,
            externalValue,
            orderBy: serializeOrdering(
              ordering.map((i) => ({ ...i, id: sortMap[i.id] || i.id }))
            ),
          },
        };
      },
    }),

    getTags: builder.query({
      query: ({ vendorId }) => `api/vendor/${vendorId}/tags/`,
      transformResponse: (response) => response.tags,
    }),

    deleteInventoryMappings: builder.mutation({
      query: ({ vendorId, capabilityId, mappingIds }) => ({
        method: 'POST',
        url: `vendors/${vendorId}/delete-selected-product-mappings-for-integration/`,
        body: {
          capabilityId,
          mappingIds,
        },
      }),
      transformResponse: camelize,
      async onQueryStarted({ queryFulfilled }) {
        try {
          await queryFulfilled;
          newToastNotification({
            body: 'Successfully deleted selected mappings',
            toastType: ToastType.SUCCESS,
          });
        } catch {
          newToastNotification({
            body: 'Failed to delete inventory mappings',
            toastType: ToastType.ERROR,
          });
        }
      },
    }),

    saveInventory: builder.mutation({
      query: ({ vendorId, changes }) => ({
        url: `vendor/${vendorId}/products/inventory-prices/multiple_update/`,
        method: 'PATCH',
        body: {
          variants: Object.keys(changes).map((key) => ({
            variant_pk: key,
            ...changes[key],
          })),
        },
      }),
      async onQueryStarted(
        { changes },
        { dispatch, queryFulfilled, getState }
      ) {
        dispatch(setMissingVariants([]));

        try {
          const { data } = await queryFulfilled;
          const { errors = [], affected = 0, missing = [] } = data;

          if (missing.length > 0) {
            dispatch(setMissingVariants(missing));
          } else {
            dispatch(setMissingVariants([]));
          }

          if (errors.length) {
            newToastNotification({
              body: joinArray(errors),
              toastType: ToastType.ERROR,
            });
          } else if (affected === 0 && missing.length > 0) {
            const matchingQueries = vendorApi.util.selectInvalidatedBy(
              getState(),
              [{ type: 'InventoryList' }]
            );

            matchingQueries.forEach(({ endpointName, originalArgs }) => {
              if (endpointName === 'getInventoryPrices') {
                dispatch(
                  vendorApi.util.updateQueryData(
                    'getInventoryPrices',
                    originalArgs,
                    (draft) => {
                      Object.entries(changes).forEach(
                        ([variantPk, updates]) => {
                          if (draft.entities[variantPk]) {
                            Object.assign(draft.entities[variantPk], updates);
                          }
                        }
                      );
                    }
                  )
                );
              }
            });

            const missingCount = missing.length;
            newToastNotification({
              body: `No variants could be updated. All ${missingCount} ${missingCount === 1 ? 'variant' : 'variants'} appear to have been removed.`,
              toastType: ToastType.ERROR,
            });
          } else if (affected > 0) {
            const matchingQueries = vendorApi.util.selectInvalidatedBy(
              getState(),
              [{ type: 'InventoryList' }]
            );

            matchingQueries.forEach(({ endpointName, originalArgs }) => {
              if (endpointName === 'getInventoryPrices') {
                dispatch(
                  vendorApi.util.updateQueryData(
                    'getInventoryPrices',
                    originalArgs,
                    (draft) => {
                      Object.entries(changes).forEach(
                        ([variantPk, updates]) => {
                          if (draft.entities[variantPk]) {
                            Object.assign(draft.entities[variantPk], updates);
                          }
                        }
                      );
                    }
                  )
                );
              }
            });

            if (missing.length > 0) {
              const missingCount = missing.length;
              const updatedCount = affected;
              newToastNotification({
                body: `${updatedCount} ${updatedCount === 1 ? 'variant' : 'variants'} updated successfully. ${missingCount} ${missingCount === 1 ? 'variant' : 'variants'} could not be found and ${missingCount === 1 ? 'was' : 'were'} skipped.`,
                toastType: ToastType.WARNING,
              });
            } else {
              newToastNotification({
                body: 'Inventory is successfully updated!',
                toastType: ToastType.SUCCESS,
              });
            }
          }
        } catch (error) {
          const errorData = error?.error?.data;

          if (errorData?.missing?.length > 0) {
            dispatch(setMissingVariants(errorData.missing));

            const matchingQueries = vendorApi.util.selectInvalidatedBy(
              getState(),
              [{ type: 'InventoryList' }]
            );

            matchingQueries.forEach(({ endpointName, originalArgs }) => {
              if (endpointName === 'getInventoryPrices') {
                dispatch(
                  vendorApi.util.updateQueryData(
                    'getInventoryPrices',
                    originalArgs,
                    (draft) => {
                      Object.entries(changes).forEach(
                        ([variantPk, updates]) => {
                          if (draft.entities[variantPk]) {
                            Object.assign(draft.entities[variantPk], updates);
                          }
                        }
                      );
                    }
                  )
                );
              }
            });
          }

          // error handled in baseQuery (toast notification already shown)
        }
      },
    }),

    saveInvoiceSettings: builder.mutation({
      query: ({ vendorId, payload }) => ({
        url: `api/vendor/${vendorId}/invoice/settings/`,
        method: 'POST',
        body: payload,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          newToastNotification({
            body: 'Invoice settings saved successfully',
            toastType: ToastType.SUCCESS,
          });
        } catch {
          // error toast handled in base query
        }
      },
    }),

    updateOrder: builder.mutation({
      // Apply PATCH response to cache (not optimistic): internal-note delete can
      // surface a previous note and always updates the nested notes list for inbox.
      async onQueryStarted(
        { cellRequestId, cellUpdateKey, getOrdersParams, order },
        { dispatch, queryFulfilled }
      ) {
        if (cellUpdateKey && cellRequestId) {
          // Ignore stale cell PATCH responses so an older save cannot overwrite
          // the latest value in the orders cache.
          latestOrderCellRequestIds.set(cellUpdateKey, cellRequestId);
        }

        try {
          const { data } = await queryFulfilled;
          if (
            cellUpdateKey &&
            cellRequestId &&
            latestOrderCellRequestIds.get(cellUpdateKey) !== cellRequestId
          ) {
            return;
          }

          if (data?.order) {
            dispatch(
              vendorApi.util.updateQueryData(
                'getOrders',
                getOrdersParams,
                (draft) => {
                  const orderToUpdate = draft.orders.find(
                    ({ id }) => id === order.id
                  );
                  if (orderToUpdate) {
                    Object.assign(orderToUpdate, camelize(data.order));
                  }
                }
              )
            );
          }
        } catch {
          // error toast handled in base query
        } finally {
          if (
            cellUpdateKey &&
            cellRequestId &&
            latestOrderCellRequestIds.get(cellUpdateKey) === cellRequestId
          ) {
            latestOrderCellRequestIds.delete(cellUpdateKey);
          }
        }
      },
      query: ({ order, url }) => ({
        body: { order },
        method: 'PATCH',
        url: actualPathUrl(url),
      }),
    }),

    updateOrderInvoiceLineItem: builder.mutation({
      query: ({ vendorId, orderedItemId, batchNumberOverride }) => ({
        url: `api/vendor/${vendorId}/order-invoice-line-items/${orderedItemId}/`,
        method: 'PATCH',
        body: {
          ordered_item: {
            batch_number_override: batchNumberOverride || null,
          },
        },
      }),
    }),

    updateOrderInvoice: builder.mutation({
      query: ({ vendorId, orderId, footerOverride }) => ({
        url: `api/vendor/${vendorId}/order-invoice/${orderId}/`,
        method: 'PATCH',
        body: {
          order: {
            footer_override: footerOverride || null,
          },
        },
      }),
    }),
  }),
});

export const {
  useBulkPostAccountingMutation,
  useBulkUpdateOrdersMutation,
  useGetBatchesQuery,
  useGetDownloadQueuesQuery,
  useLazyGetBrandsQuery,
  useLazyGetCategoriesQuery,
  useLazyExportCsvQuery,
  useLazyGetSizesQuery,
  useLazyGetStockLocationsQuery,
  useLazyGetProductAttributesQuery,
  useLazyGetStrainsQuery,
  useLazyGetInventoryImportCapabilitiesQuery,
  useLazyGetIntegrationMappingsQuery,
  useLazyGetTagsQuery,
  useGetInventoryPricesQuery,
  useGetLogEntriesQuery,
  useGetOrdersQuery,
  useGetOrdersCountQuery,
  useGetProductsQuery,
  useGetSettingsQuery,
  useGetSyncStatusWebSocketUrlQuery,
  useGetFSOUserActivityWebSocketUrlQuery,
  useGetInvoiceSettingsQuery,
  useGetOrderInvoiceQuery,
  useSaveInventoryMutation,
  useDeleteInventoryMappingsMutation,
  useLazyGetInvoiceSettingsQuery,
  useLazyGetOrderInvoiceQuery,
  useSaveInvoiceSettingsMutation,
  useUpdateOrderMutation,
  useUpdateOrderInvoiceLineItemMutation,
  useUpdateOrderInvoiceMutation,
} = vendorApi;
